import { Helmet } from 'react-helmet-async';
import type { Thing } from 'schema-dts';
import { absoluteUrl, SITE_NAME, organizationSchema, websiteSchema } from '@/lib/seo';

interface MetaProps {
  title: string;
  description?: string;
  image?: string;
  path?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  authors?: string[];
  jsonLd?: Thing | Thing[];
}

/**
 * Per-page <head> manager. Sets title, description, canonical, Open Graph,
 * Twitter card, and JSON-LD structured data.
 */
export function Meta({
  title,
  description,
  image = '/og/default.png',
  path = '/',
  type = 'website',
  publishedTime,
  authors,
  jsonLd,
}: MetaProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
  const url = absoluteUrl(path);
  const img = image.startsWith('http') ? image : absoluteUrl(image);
  const ldArray: Thing[] = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [organizationSchema(), websiteSchema()];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' &&
        authors?.map((a) => <meta key={a} property="article:author" content={a} />)}

      <script type="application/ld+json">{JSON.stringify(ldArray)}</script>
    </Helmet>
  );
}